import { iconPaths } from '@design-system/atoms/icon';
import { Link } from '@design-system/atoms/link';
import { Modal } from '@design-system/molecules/modal';
import { Navigation } from '@design-system/molecules/navigation';
import ThemeProvider from '@design-system/theme/ThemeContext';
import { ReduxToastProvider } from '@pages/auth/layout/ReduxToastProvider';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { OverlayProvider } from 'react-aria';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { getAccountInfo, isAuthenticated, doLogout } from 'src/actions/login'; // eslint-disable-line
//import { setTestAccountDrawerShowFlag } from 'src/actions/testAccount';
import { PDFViewer } from 'src/components/common/PDFViewer';
import { AppState } from 'src/reducers';
import { Avatar } from '@design-system/atoms/avatar/Avatar';
import { IconButton } from '@design-system/atoms/icon-button';
import { ActionMenu } from '@design-system/molecules/action-menu';
import { Routes } from 'src/routes/newRoutes';
import stylesConfig, { appConfig } from 'src/config';
import { useAuthenticatedLayout } from './layout.hooks';
import { DoorOpenIcon } from '@phosphor-icons/react';
import useHopscotch from 'src/hooks/useHopscotch';
import ScrollToTopButton from 'src/components/common/ScrollToTopButton';
import { SupportedLang } from 'src/messages';
import { useOauth2Client } from 'src/oauth2/oauth2.hooks';
import { Button } from '@design-system/atoms/button';
import { Typography } from '@design-system/atoms/typography';
import { Symbol, SymbolType } from '@design-system/atoms/symbol';
import * as S from '../layout/layout.styles';
import { leavePreviewMode } from 'src/actions/appearance';

const TermsConditionsDialog = () => {
  const location = useLocation();
  const history = useHistory();
  const { formatMessage } = useIntl();
  const appLang = useSelector((state: AppState) => state.locale.lang);

  const hash = location.hash?.substring(1);

  const handleClose = () => {
    // Navigate to the same path but without the hash
    history.push(location.pathname + location.search);
  };

  return (
    <Modal
      isOpen={Boolean(hash)}
      width={900}
      onClose={handleClose}
      title={formatMessage({ id: 'dashboard.terms.conditions' })}
    >
      <PDFViewer
        file={
          hash !== 'data-processing'
            ? appLang === SupportedLang.DE
              ? '/documents/TermsConditions_DE.pdf'
              : appLang === SupportedLang.FR
                ? '/documents/TermsConditions_FR.pdf'
                : '/documents/TermsConditions_EN.pdf'
            : appLang === SupportedLang.FR
              ? '/documents/DataProcessing_FR.pdf'
              : '/documents/DataProcessing_EN.pdf'
        }
        zoom={1.28}
      />
    </Modal>
  );
};

const UserInfo = () => {
  const [isOpen, setIsOpen] = useState(false);
  const userData = useSelector((state: AppState) => state.login.userData);
  const userFullName = `${userData?.firstName ?? ''} ${userData?.lastName ?? ''}`.trim();
  const avatarRef = useRef<HTMLDivElement>(null);
  const { location, push } = useHistory();
  const intl = useIntl();
  const dispatch = useDispatch();
  const { updatePasswordRedirect } = useOauth2Client();

  const onToggleMenu = () => {
    setIsOpen((prev) => !prev);
  };

  const onProfileClick = () => {
    push(Routes.ACCOUNT_PROFILE);
    setIsOpen(false);
  };

  return (
    <section>
      <Avatar ref={avatarRef} name={userFullName} size={36} onPress={onToggleMenu} data-testid="user-avatar" />
      <ActionMenu
        title={userFullName}
        triggerRef={avatarRef}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        data-testid="user-action-menu"
      >
        <ActionMenu.Item
          id="menu-user-go-to-profile"
          label={intl.formatMessage({ id: 'nav.account.profile' })}
          isActive={location.pathname === Routes.ACCOUNT_PROFILE}
          onPress={onProfileClick}
          startIcon="edit"
          data-testid="menu-user-profile"
        />
        <ActionMenu.Item
          id="menu-user-change-password"
          label={intl.formatMessage({ id: 'dialog.user.info.menu.button.change.password' })}
          onPress={() => updatePasswordRedirect()}
          startIcon="policy"
          data-testid="menu-user-change-password"
        />
        <ActionMenu.Item
          id="menu-user-logout"
          label={intl.formatMessage({ id: 'dialog.user.info.menu.button.logout' })}
          onPress={() => {
            dispatch(doLogout());
          }}
          startIcon={DoorOpenIcon}
          isDanger
          data-testid="menu-user-logout"
        />
      </ActionMenu>
    </section>
  );
};

export const AuthenticatedLayout = ({ children }: { children: ReactNode }) => {
  const { menuData, footerLinksData } = useAuthenticatedLayout();
  const logo = useSelector((state: AppState) => state.app.logo);
  const isPreviewMode = useSelector((state: AppState) => state.appearance.previewMode);
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  // Validate the backend session once when the authenticated layout mounts.
  // The thunk dispatches doLogout() on failure, which – combined with the
  // global 401 interceptors – ensures the UI does not render protected
  // content while the backend session is already invalid.
  useEffect(() => {
    dispatch(isAuthenticated());
  }, [dispatch]);

  useHopscotch();

  // const accountInfoUrl = useSelector((state: AppState) => state.login.userData._links.account_info.href);
  // const dispatch = useDispatch();

  // const handleOnCodeButtonClick = () => {
  //   dispatch(getAccountInfo());
  //   dispatch(setTestAccountDrawerShowFlag(true));
  // };

  return (
    <ThemeProvider initialTheme="light">
      <OverlayProvider>
        <ReduxToastProvider>
          <TermsConditionsDialog />
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              backgroundColor: '#F8F8F7',
              height: '100vh',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <Navigation>
              <Navigation.Header
                logo={{
                  sygnet: logo,
                  regular: logo,
                  alt: stylesConfig.LOGO_ALT,
                  href: stylesConfig.LOGO_LINK,
                }}
              />

              <Navigation.User>
                <UserInfo />
                <IconButton
                  icon="code"
                  variant="circle"
                  size={18}
                  as="a"
                  href={appConfig.CLICKUP_DOCUMENTAION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              </Navigation.User>

              <Navigation.Items>
                {menuData.map((item, index) => (
                  <Navigation.Item {...item} key={index} icon={item.icon as keyof typeof iconPaths} />
                ))}
              </Navigation.Items>

              <Navigation.Footer>
                {footerLinksData.map((link, index) => (
                  <Link variant="subtle" size={12} key={index} href={link.href}>
                    {link.label}
                  </Link>
                ))}
              </Navigation.Footer>
            </Navigation>
            <main
              style={{
                padding: '24px',
                flexGrow: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              {isPreviewMode && (
                <>
                  <S.Information>
                    <Symbol type={SymbolType.INFORMATION} />
                    <Typography variant="body-12" as="p">
                      {formatMessage({ id: 'dashboard.info.preview.mode' })}
                    </Typography>
                    <Button size="small" color="primary" onClick={() => dispatch(leavePreviewMode())}>
                       {formatMessage({ id: 'dashboard.button.leave.preview.mode' })}
                    </Button>
                  </S.Information>
                </>
              )}
              {children}
              <ScrollToTopButton />
            </main>
          </div>
        </ReduxToastProvider>
      </OverlayProvider>
    </ThemeProvider>
  );
};
